import { useState, useRef, useEffect } from 'react';
import { ChevronDown, Search, Filter } from 'lucide-react';
import { Checkbox } from './checkbox';
import { Input } from './input';
import { Button } from './button';
import { cn } from '../../lib/utils';

export interface ExcelFilterOption {
  value: string;
  label: string;
}

interface ExcelFilterDropdownProps {
  /** Nom del filtre, p.ex. "Estat" — es mostra al botó quan no hi ha cap valor marcat. */
  label: string;
  options: ExcelFilterOption[];
  /** Valors actualment aplicats. Buit (o tots marcats) es tracta com "sense filtre". */
  selected: string[];
  onApply: (values: string[]) => void;
  className?: string;
  /** Sobreescriu la mida/padding del botó (p.ex. per igualar-lo a uns
   *  `<select>` nadius veïns) — es fusiona amb twMerge, no s'acumula. */
  buttonClassName?: string;
}

/**
 * Desplegable de filtre "a l'estil Excel": quadres de check per a cada
 * valor, cerca, Seleccionar-ho tot / Netejar, i D'acord / Cancel·lar (els
 * canvis nomes s'apliquen en prémer D'acord — igual que un autofiltre
 * d'Excel). Component genèric, reutilitzable a qualsevol filtre de llista.
 */
export function ExcelFilterDropdown({ label, options, selected, onApply, className = '', buttonClassName }: ExcelFilterDropdownProps) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<Set<string>>(new Set(selected));
  const [search, setSearch] = useState('');
  const wrapperRef = useRef<HTMLDivElement>(null);

  const openPanel = () => {
    setDraft(new Set(selected));
    setSearch('');
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return;
    const onClickOutside = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onEscape = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onClickOutside);
    document.addEventListener('keydown', onEscape);
    return () => {
      document.removeEventListener('mousedown', onClickOutside);
      document.removeEventListener('keydown', onEscape);
    };
  }, [open]);

  const filteredOptions = options.filter((o) => o.label.toLowerCase().includes(search.trim().toLowerCase()));
  const allChecked = options.length > 0 && options.every((o) => draft.has(o.value));
  const someChecked = options.some((o) => draft.has(o.value)) && !allChecked;

  const toggleOption = (value: string) => {
    setDraft((prev) => {
      const next = new Set(prev);
      if (next.has(value)) next.delete(value); else next.add(value);
      return next;
    });
  };
  const toggleAll = () => {
    setDraft((prev) => (allChecked ? new Set() : new Set(options.map((o) => o.value))));
  };

  const applyAndClose = () => {
    // Tot marcat equival a "sense filtre" — es desa buit perquè la crida a
    // l'API no enviï cap paràmetre status (mateixa semàntica que "all").
    onApply(allChecked ? [] : Array.from(draft));
    setOpen(false);
  };
  const cancelAndClose = () => setOpen(false);

  const buttonText = selected.length === 0 || selected.length === options.length
    ? `${label}: Tots`
    : selected.length === 1
      ? `${label}: ${options.find((o) => o.value === selected[0])?.label ?? selected[0]}`
      : `${label} (${selected.length})`;

  return (
    <div ref={wrapperRef} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => (open ? setOpen(false) : openPanel())}
        className={cn('flex items-center gap-1.5 px-2.5 py-1.5 border rounded-lg bg-background hover:bg-muted/50 transition-colors text-xs', buttonClassName)}
      >
        <Filter className="h-3 w-3 text-muted-foreground shrink-0" />
        <span className="truncate max-w-[140px]">{buttonText}</span>
        <ChevronDown className="h-3 w-3 text-muted-foreground shrink-0" />
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-64 bg-white border rounded-lg shadow-lg">
          <div className="p-2 border-b">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                autoFocus
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Cercar..."
                className="h-8 pl-7 text-xs"
              />
            </div>
          </div>

          <div className="px-2 py-1.5 border-b">
            <label className="flex items-center gap-2 px-1 py-1 text-xs cursor-pointer select-none hover:bg-muted/50 rounded">
              <Checkbox
                checked={allChecked ? true : someChecked ? 'indeterminate' : false}
                onCheckedChange={toggleAll}
              />
              <span className="font-medium">(Seleccionar-ho tot)</span>
            </label>
          </div>

          <div className="max-h-56 overflow-y-auto py-1">
            {filteredOptions.length === 0 ? (
              <p className="px-3 py-3 text-xs text-muted-foreground text-center">Sense resultats</p>
            ) : (
              filteredOptions.map((o) => (
                <label key={o.value} className="flex items-center gap-2 px-3 py-1.5 text-xs cursor-pointer select-none hover:bg-muted/50">
                  <Checkbox checked={draft.has(o.value)} onCheckedChange={() => toggleOption(o.value)} />
                  <span className="truncate">{o.label}</span>
                </label>
              ))
            )}
          </div>

          <div className="flex justify-end gap-2 px-2 py-2 border-t">
            <Button type="button" size="sm" variant="outline" onClick={cancelAndClose} className="h-7 text-xs px-2.5">
              Cancel·lar
            </Button>
            <Button type="button" size="sm" onClick={applyAndClose} className="h-7 text-xs px-2.5">
              D&apos;acord
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
